import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchObservabilityEvents, type ObservabilityEvent } from "../lib/api";
import { useDocumentTitle } from "../hooks/use-document-title";

const PAGE_SIZE = 50;

interface EventTypeOption {
  value: string;
  isNew?: boolean;
}

const EVENT_TYPES: EventTypeOption[] = [
  { value: "message_inbound" },
  { value: "message_outbound" },
  { value: "bid_recommended" },
  { value: "bid_placed" },
  { value: "bid_failed" },
  { value: "recommendation_sent" },
  { value: "recommendation_scheduled", isNew: true },
  { value: "recommendation_suppressed" },
  { value: "outbid_alert_suppressed", isNew: true },
  { value: "stock_alert_suppressed", isNew: true },
  { value: "personalised_notification_suppressed", isNew: true },
  { value: "recommendation_feedback", isNew: true },
  { value: "stock_insights_updated", isNew: true },
  { value: "preference_updated" },
  { value: "preference_flow_started", isNew: true },
  { value: "escalation_triggered" },
  { value: "identity_resolved" },
  { value: "identity_conflict" },
  { value: "workflow_failure" },
  { value: "agent_invocation" },
  { value: "review_flagged" },
];

function outcomeBadge(outcome: string): string {
  switch (outcome) {
    case "success":
      return "badge badge-success";
    case "failure":
      return "badge badge-error";
    case "suppressed":
      return "badge badge-warning";
    default:
      return "badge badge-neutral";
  }
}

export function ObservabilityPage() {
  useDocumentTitle("Observability");
  const [filterType, setFilterType] = useState("");
  const [filterDealer, setFilterDealer] = useState("");
  const [events, setEvents] = useState<ObservabilityEvent[]>([]);
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<ObservabilityEvent | null>(null);

  const loadPage = useCallback(
    async (cursor: string | undefined) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchObservabilityEvents({
          eventType: filterType || undefined,
          dealerId: filterDealer || undefined,
          cursor,
          limit: PAGE_SIZE,
        });
        setEvents(result.events);
        setNextCursor(result.nextCursor);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    },
    [filterType, filterDealer],
  );

  useEffect(() => {
    setPageIndex(0);
    setCursors([null]);
    void loadPage(undefined);
  }, [loadPage]);

  function handleNext() {
    if (!nextCursor) return;
    const newPageIndex = pageIndex + 1;
    const newCursors = [...cursors];
    if (newCursors.length <= newPageIndex) {
      newCursors.push(nextCursor);
    }
    setCursors(newCursors);
    setPageIndex(newPageIndex);
    void loadPage(nextCursor);
  }

  function handlePrev() {
    if (pageIndex <= 0) return;
    const newPageIndex = pageIndex - 1;
    setPageIndex(newPageIndex);
    void loadPage(cursors[newPageIndex] ?? undefined);
  }

  return (
    <>
      <div className="card-header">
        <h1 className="page-title">Observability</h1>
        <button
          className="btn btn-sm"
          type="button"
          onClick={() => {
            setPageIndex(0);
            setCursors([null]);
            void loadPage(undefined);
          }}
        >
          Refresh
        </button>
      </div>

      <div className="filter-bar">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{ width: "auto" }}
        >
          <option value="">All event types</option>
          {EVENT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.value}
              {t.isNew ? " (new)" : ""}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Filter by dealer ID"
          value={filterDealer}
          onChange={(e) => setFilterDealer(e.target.value)}
          style={{ width: 160 }}
        />
      </div>

      {loading && <div className="loading">Loading events...</div>}
      {error && <div className="error-msg">{error}</div>}

      {!loading && events.length === 0 && (
        <div className="empty-state">No events found</div>
      )}

      {events.length > 0 && (
        <>
          <table>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Event Type</th>
                <th>Dealer</th>
                <th>Outcome</th>
                <th>Tokens</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr
                  key={event.eventId}
                  className="clickable-row"
                  onClick={() => setSelectedEvent(event)}
                >
                  <td className="mono">
                    {new Date(event.timestamp).toLocaleString()}
                  </td>
                  <td className="mono">{event.eventType}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {event.dealerId ? (
                      <Link
                        to={`/dealer/${event.dealerId}`}
                        className="dealer-link"
                      >
                        #{event.dealerId}
                      </Link>
                    ) : (
                      "\u2014"
                    )}
                  </td>
                  <td>
                    <span className={outcomeBadge(event.outcome)}>
                      {event.outcome}
                    </span>
                    {event.outcomeReason && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 11,
                          color: "var(--color-text-secondary)",
                        }}
                      >
                        {event.outcomeReason}
                      </span>
                    )}
                  </td>
                  <td className="mono">
                    {event.tokenUsage
                      ? `${event.tokenUsage.inputTokens}/${event.tokenUsage.outputTokens}`
                      : "\u2014"}
                  </td>
                  <td className="mono">
                    {event.tokenUsage
                      ? `$${event.tokenUsage.estimatedCostUsd.toFixed(4)}`
                      : "\u2014"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 12,
              fontSize: 13,
            }}
          >
            <button
              className="btn btn-sm"
              type="button"
              onClick={handlePrev}
              disabled={pageIndex === 0}
            >
              Previous
            </button>
            <span style={{ color: "var(--color-text-secondary)" }}>
              Page {pageIndex + 1}
            </span>
            <button
              className="btn btn-sm"
              type="button"
              onClick={handleNext}
              disabled={!nextCursor}
            >
              Next
            </button>
          </div>
        </>
      )}

      {selectedEvent && (
        <EventDetailDrawer event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </>
  );
}

function EventDetailDrawer({
  event,
  onClose,
}: {
  event: ObservabilityEvent;
  onClose: () => void;
}) {
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer">
        <div className="drawer-header">
          <div>
            <div className="drawer-title">{event.eventType}</div>
            <div className="drawer-subtitle mono">
              {new Date(event.timestamp).toLocaleString()}
            </div>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="drawer-body">
          <div className="drawer-section">
            <div className="drawer-section-title">Summary</div>
            <div className="drawer-summary-grid">
              <div className="drawer-summary-label">Outcome</div>
              <div>
                <span className={`badge badge-${outcomeBadgeKey(event.outcome)}`}>
                  {event.outcome}
                </span>
                {event.outcomeReason && (
                  <span className="drawer-outcome-reason">{event.outcomeReason}</span>
                )}
              </div>
              {event.dealerId !== null && (
                <>
                  <div className="drawer-summary-label">Dealer</div>
                  <div>
                    <Link to={`/dealer/${event.dealerId}`} className="dealer-link">
                      #{event.dealerId}
                    </Link>
                  </div>
                </>
              )}
              {event.conversationId && (
                <>
                  <div className="drawer-summary-label">Conversation</div>
                  <div className="mono drawer-mono-wrap">{event.conversationId}</div>
                </>
              )}
              <div className="drawer-summary-label">Event ID</div>
              <div className="mono drawer-mono-wrap">{event.eventId}</div>
            </div>
          </div>

          {event.tokenUsage && (
            <div className="drawer-section">
              <div className="drawer-section-title">LLM cost</div>
              <div className="drawer-summary-grid">
                <div className="drawer-summary-label">Model</div>
                <div className="mono drawer-mono-wrap">{event.tokenUsage.model}</div>
                <div className="drawer-summary-label">Input tokens</div>
                <div className="mono">{event.tokenUsage.inputTokens.toLocaleString()}</div>
                <div className="drawer-summary-label">Output tokens</div>
                <div className="mono">{event.tokenUsage.outputTokens.toLocaleString()}</div>
                <div className="drawer-summary-label">Estimated cost</div>
                <div className="mono">
                  ${event.tokenUsage.estimatedCostUsd.toFixed(6)}
                </div>
              </div>
            </div>
          )}

          <div className="drawer-section">
            <div className="drawer-section-title">Data</div>
            {Object.keys(event.data).length === 0 ? (
              <div className="empty-state" style={{ padding: "12px 0" }}>
                No data payload
              </div>
            ) : (
              <pre className="drawer-json">{JSON.stringify(event.data, null, 2)}</pre>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

function outcomeBadgeKey(outcome: string): "success" | "error" | "warning" | "neutral" {
  switch (outcome) {
    case "success":
      return "success";
    case "failure":
      return "error";
    case "suppressed":
      return "warning";
    default:
      return "neutral";
  }
}
